import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from '@emotion/styled';
import { Button, Typography } from '@kep-ui-kit/ui-components';
import { useTranslation } from 'react-i18next';
import { FormikProps, useFormik } from 'formik';
import * as Yup from 'yup';
import { Helmet } from 'react-helmet';
import { withErrorHandling, IWrappedComponentProps } from '@selfserve/react-hocs';
import { useStorage, storage, storageKeys, useQueryParams } from '@selfserve/react-hooks';
import {
  StyledLogo,
  StyledV2PageWrapper,
  StyledV2PageBackground,
  StyledV2GlassCard,
  StyledV2Input,
  StyledV2Link,
} from '@selfserve/react-ui';
import {
  routesPaths,
  getRecaptchaToken,
  IIndexSignature,
  apiStatuses,
  responseTypes,
  socialType,
  sendFunnelAnalytics,
} from '@selfserve/react-utils';
import {
  useEmailValidationRequest,
  usePackageInfoRequest,
  SocialRegistration,
} from '@selfserve/react-feature-get-started';

export interface FormValuesV2 {
  email: string;
}

const ALREADY_REGISTERED_ERROR_ID = 10019;

const StyledTitleBlock = styled.div(({ theme }: { theme?: any }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  width: '100%',
  textAlign: 'center',
  color: theme.palette.text.primary,
}));

const StyledFormArea = styled.div(({ theme }: { theme?: any }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(3),
  width: '100%',
}));

const StyledFieldGroup = styled.div(({ theme }: { theme?: any }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  width: '100%',
}));

const StyledLabelInputGroup = styled.div(({ theme }: { theme?: any }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  width: '100%',
}));

// Button size XL which not exists in the current version of @kep-ui-kit/ui-components
const StyledSubmitBtn = styled(Button)(() => ({
  fontSize: '16px',
  height: '48px',
  padding: '16px',
}));

interface GetStartedV2Props extends IWrappedComponentProps {}

const GetStartedV2Component: React.FC<GetStartedV2Props> = ({ openErrorModal }) => {
  const navigate = useNavigate();
  const { desiredPackageId } = useParams();
  const queryParams = useQueryParams();
  const { t } = useTranslation();
  const [isSocialLoading, setIsSocialLoading] = useState(false);
  const [validateOnBlur, setValidateOnBlur] = useState(false);
  const [validateOnChange, setValidateOnChange] = useState(false);
  const [isRecaptchaLoading, setIsRecaptchaLoading] = useState(false);

  const [currentExistingUser, setExistingUser] = useStorage(
    storage.LOCAL,
    storageKeys.EXISTING_USER,
    false
  );
  const [, setSessionEmail] = useStorage(
    storage.SESSION,
    storageKeys.EMAIL,
    ''
  );
  const [, setSessionPackageId] = useStorage(
    storage.SESSION,
    storageKeys.PACKAGE_ID,
    desiredPackageId
  );

  const {
    data: emailValidationResponse,
    isLoading: isRequestLoading,
    mutateAsync: handleEmailValidationRequest,
    isSuccess,
  } = useEmailValidationRequest();

  const { data: packageInfoResponse } = usePackageInfoRequest(
    desiredPackageId as string
  );

  const isLoading = isRequestLoading || isRecaptchaLoading || isSocialLoading;

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        email: Yup.string()
          .email(t('common.validations.email-valid'))
          .required(t('common.validations.email-required')),
      }),
    [t]
  );

  const formik: FormikProps<FormValuesV2> = useFormik<FormValuesV2>({
    validateOnBlur,
    validateOnChange,
    initialValues: { email: '' },
    validationSchema,
    onSubmit: async (values) => {
      if (!isLoading) {
        setIsRecaptchaLoading(true);
        try {
          const recaptchaToken: string = await getRecaptchaToken();
          const payload: IIndexSignature<string | undefined> = {
            ...values,
            ...queryParams,
            desiredPackageId,
            recaptchaToken,
          };
          handleEmailValidationRequest(payload);
          setSessionEmail(values.email);
          setSessionPackageId(desiredPackageId as string);
        } finally {
          setIsRecaptchaLoading(false);
        }
      }
    },
  });

  const {
    errors,
    values,
    touched,
    handleChange,
    handleBlur,
    handleSubmit,
    setFieldError,
  } = formik;

  useEffect(() => {
    if (formik.dirty && !validateOnBlur) setValidateOnBlur(true);
  }, [formik.dirty, validateOnBlur]);

  useEffect(() => {
    if (errors.email && !validateOnChange) {
      setValidateOnChange(true);
    } else if (!errors.email) {
      setValidateOnChange(false);
    }
  }, [errors.email, validateOnChange]);

  useEffect(() => {
    if (!isSuccess) return;
    const { status, data } = emailValidationResponse || {};
    if (String(status).startsWith(responseTypes.SUCCESS)) {
      const { url, existingUser } = data || {};
      if (existingUser) {
        setExistingUser(existingUser);
      } else if (currentExistingUser) {
        setExistingUser(false);
      }
      if (url) {
        window.location.href = url;
      } else {
        navigate(routesPaths.VERIFY_V2);
      }
    } else {
      const { message_en } = data || {};
      setFieldError('email', message_en);
      if (data?.id === ALREADY_REGISTERED_ERROR_ID) {
        openErrorModal({
          url: routesPaths.LOGIN_V2,
          text: t(`errors.${ALREADY_REGISTERED_ERROR_ID}`),
          confirmBtnText: t('common.login'),
          preventActionsOnClose: true,
          withDissmiss: true,
        });
      } else if (data?.statusCode === apiStatuses.BAD_REQUEST) {
        setFieldError('email', data?.message?.join('\n'));
      }
    }
  }, [emailValidationResponse, isSuccess]);

  useEffect(() => {
    sendFunnelAnalytics(
      { type: 'pageLoad', page: '', event: 'signupCapture' },
      desiredPackageId
    );
  }, []);

  const showSocialLogin = !!packageInfoResponse?.data?.enableSocialLoginFlow;
  const socialOptions =
    packageInfoResponse?.data?.packageAdditionalData?.socialRegistrationOptions ||
    [socialType.GOOGLE];

  return (
    <>
      <Helmet>
        <title>{t('titles.create-account')}</title>
      </Helmet>
      <StyledV2PageWrapper>
        <StyledV2PageBackground centered className="container">
          <StyledV2GlassCard>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '24px' }}>
              <StyledLogo />
              <StyledTitleBlock>
              <Typography variant="h2" textAlign={'center'}>
                {t('get-started-v2.avatar-studio-title')} 🎉
              </Typography>
              <Typography variant="body1">
                {t('get-started-v2.avatar-studio-subtitle')}
              </Typography>
            </StyledTitleBlock>
            </div>

            <StyledFormArea>
              {showSocialLogin && (
                <SocialRegistration
                  onLoadingChange={setIsSocialLoading}
                  openErrorModal={openErrorModal}
                  setFieldError={setFieldError}
                  loading={isLoading}
                  options={socialOptions}
                />
              )}

              <form onSubmit={handleSubmit}>
                <StyledFieldGroup>
                  <StyledLabelInputGroup>
                    <Typography variant={"body1Highlight" as any}>
                      {t('create-account-v2.work-email')}
                    </Typography>
                    <StyledV2Input
                      id="email"
                      name="email"
                      type="email"
                      value={values.email}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      hasError={touched.email && !!errors.email}
                      autoComplete="email"
                      autoFocus
                    />
                    {touched.email && errors.email && (
                      <Typography variant={"formError" as any} sx={{ mt: 0.5, color: (theme: any) => theme.palette.danger.main, fontWeight: 700 }}>
                        {errors.email}
                      </Typography>
                    )}
                  </StyledLabelInputGroup>
                  <StyledSubmitBtn
                    type="submit"
                    disabled={isLoading}
                    onClick={() => {
                      if (!errors.email) {
                        sessionStorage.removeItem(storageKeys.SOCIAL_TYPE);
                        sendFunnelAnalytics(
                          {
                            type: 'buttonClicked',
                            page: '',
                            event: 'signupMethodSelected',
                            properties: { method: 'manual' },
                          },
                          desiredPackageId
                        );
                      }
                    }}
                  >
                    {t('get-started-v2.create-account-button')}
                  </StyledSubmitBtn>
                </StyledFieldGroup>
              </form>

              <Typography variant="body1" textAlign="center">
                {t('get-started-v2.already-have-an-account')}{' '}
                <StyledV2Link href={routesPaths.LOGIN_V2}>
                  {t('common.login')}
                </StyledV2Link>
              </Typography>
            </StyledFormArea>
          </StyledV2GlassCard>
        </StyledV2PageBackground>
      </StyledV2PageWrapper>
    </>
  );
};

export const GetStartedV2 = withErrorHandling<unknown>(GetStartedV2Component);
